import React, { useCallback, useEffect, useId, useRef, useState } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import CheckoutMap from "./CheckoutMap";
import { formatSavedAddressForOrder } from "../utils/checkoutAddressFormat";
import {
  CONTACT_DIAL_OPTIONS,
  ContactDialFlagImg,
  dialMetaForCode,
  parsePhoneForEdit,
} from "../../../utils/phoneDialCountries";

const EMPTY = {
  fullName: "",
  phone: "",
  email: "",
  city: "",
  area: "",
  street: "",
  building: "",
  notes: "",
  label: "",
  latitude: "",
  longitude: "",
};

export default function CheckoutAddressModal({
  isOpen,
  onClose,
  onSaved,
  defaultFullName = "",
  defaultPhone = "",
  defaultEmail = "",
  token,
  guestMode = false,
  onGuestApply,
  /** When reopening guest modal, restore fields + dial code (memory-only in parent). */
  guestInitialSnapshot = null,
}) {
  const { t } = useTranslation("global");
  const [draft, setDraft] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [phoneDialCode, setPhoneDialCode] = useState("+966");
  const [phoneDialOpen, setPhoneDialOpen] = useState(false);
  const dialWrapRef = useRef(null);
  const dialListId = useId();
  const defaultsRef = useRef({ defaultFullName, defaultPhone, defaultEmail });
  defaultsRef.current = { defaultFullName, defaultPhone, defaultEmail };

  /* Run when the dialog opens (or guest snapshot changes while closed). Do not depend on checkout field props or reopening will reset while the user types in the modal. */
  useEffect(() => {
    if (!isOpen) return;
    setPhoneDialOpen(false);
    if (guestMode && guestInitialSnapshot?.draft) {
      const snap = guestInitialSnapshot.draft;
      const dial = guestInitialSnapshot.phoneDialCode || "+966";
      setPhoneDialCode(dial);
      setDraft({
        ...EMPTY,
        ...snap,
        phone: String(snap.phone || "").replace(/[^\d\s]/g, ""),
      });
      return;
    }
    const { defaultFullName: df, defaultPhone: dp, defaultEmail: de } =
      defaultsRef.current;
    const fromDefault = (dp || "").trim();
    const parsed = parsePhoneForEdit(fromDefault);
    setPhoneDialCode(parsed.dialCode);
    setDraft({
      ...EMPTY,
      fullName: df || "",
      phone: parsed.national,
      email: de || "",
    });
  }, [isOpen, guestMode, guestInitialSnapshot]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const html = document.documentElement;
    const body = document.body;
    const prevHtmlOverflow = html.style.overflow;
    const prevBodyOverflow = body.style.overflow;
    const prevBodyTouch = body.style.touchAction;
    html.style.overflow = "hidden";
    body.style.overflow = "hidden";
    body.style.touchAction = "none";
    return () => {
      html.style.overflow = prevHtmlOverflow;
      body.style.overflow = prevBodyOverflow;
      body.style.touchAction = prevBodyTouch;
    };
  }, [isOpen]);

  useEffect(() => {
    if (!phoneDialOpen) return;
    const onDocMouseDown = (e) => {
      if (dialWrapRef.current && !dialWrapRef.current.contains(e.target)) {
        setPhoneDialOpen(false);
      }
    };
    const onKey = (e) => {
      if (e.key === "Escape") setPhoneDialOpen(false);
    };
    document.addEventListener("mousedown", onDocMouseDown);
    window.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDocMouseDown);
      window.removeEventListener("keydown", onKey);
    };
  }, [phoneDialOpen]);

  const handleMapLocationUpdate = useCallback((lat, lng) => {
    setDraft((prev) => ({
      ...prev,
      latitude: lat,
      longitude: lng,
    }));
  }, []);

  const clearMapPin = useCallback(() => {
    setDraft((prev) => ({ ...prev, latitude: "", longitude: "" }));
  }, []);

  if (!isOpen) return null;

  const set = (field, value) =>
    setDraft((prev) => ({ ...prev, [field]: value }));

  const hasMapPin =
    String(draft.latitude || "").trim() && String(draft.longitude || "").trim();

  const handleSubmit = async (e) => {
    e.preventDefault();
    const phoneDigits = String(draft.phone || "")
      .replace(/\D/g, "")
      .replace(/^0+/, "");
    if (phoneDigits.length < 8) {
      toast.error(
        "Enter a valid phone number (at least 8 digits after the country code).",
      );
      return;
    }
    const phoneCombined = `${phoneDialCode} ${phoneDigits}`;
    const latP = Number.parseFloat(String(draft.latitude || ""));
    const lngP = Number.parseFloat(String(draft.longitude || ""));
    const hasValidCoords =
      hasMapPin && Number.isFinite(latP) && Number.isFinite(lngP);

    if (guestMode) {
      try {
        setSaving(true);
        const addressText = formatSavedAddressForOrder({
          city: draft.city.trim(),
          area: draft.area.trim(),
          street: draft.street.trim(),
          building: draft.building.trim(),
          notes: draft.notes.trim(),
        });
        onGuestApply?.({
          fullName: draft.fullName.trim(),
          phone: phoneCombined,
          email: draft.email.trim(),
          address: addressText,
          latitude: hasValidCoords ? String(latP) : "",
          longitude: hasValidCoords ? String(lngP) : "",
          snapshot: {
            draft: { ...draft, phone: phoneDigits },
            phoneDialCode,
          },
        });
        toast.success(
          t("store.checkout.guestAddressApplied", {
            defaultValue: "Delivery details applied for this visit.",
          }),
        );
        onClose();
      } finally {
        setSaving(false);
      }
      return;
    }

    if (!token) {
      toast.error("Session expired. Sign in again.");
      return;
    }
    try {
      setSaving(true);
      const { data } = await axios.post(
        "/auth/addresses",
        {
          fullName: draft.fullName.trim(),
          phone: phoneCombined,
          email: draft.email.trim(),
          city: draft.city.trim(),
          area: draft.area.trim(),
          street: draft.street.trim(),
          building: draft.building.trim(),
          notes: draft.notes.trim(),
          label: draft.label.trim(),
          ...(hasValidCoords ? { latitude: latP, longitude: lngP } : {}),
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success("Address saved.");
      onSaved?.(data?.address, data?.savedAddresses);
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.message || "Could not save address.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="store-checkout-address-modal-overlay" role="presentation">
      <button
        type="button"
        className="store-checkout-address-modal-backdrop"
        aria-label="Close"
        onClick={onClose}
      />
      <div
        className="store-checkout-address-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="store-checkout-address-modal-title"
      >
        <div className="store-checkout-address-modal-head">
          <h2 id="store-checkout-address-modal-title">
            {guestMode
              ? t("store.checkout.guestAddressModalTitle", {
                  defaultValue: "Delivery location",
                })
              : t("store.checkout.newAddressModalTitle", {
                  defaultValue: "New delivery address",
                })}
          </h2>
          <button
            type="button"
            className="store-checkout-address-modal-close"
            onClick={onClose}
            aria-label="Close"
          >
            ×
          </button>
        </div>
        <div className="store-checkout-address-modal-scroll">
          <form
            onSubmit={handleSubmit}
            className="store-checkout-address-modal-body"
          >
            <div className="store-form-group">
              <label htmlFor="addr-label" className="store-form-label">
                Label (optional)
              </label>
              <input
                id="addr-label"
                placeholder="Home, Work…"
                value={draft.label}
                onChange={(e) => set("label", e.target.value)}
                maxLength={40}
              />
            </div>
            <div className="store-form-group">
              <label htmlFor="addr-fullName" className="store-form-label">
                Full name
              </label>
              <input
                id="addr-fullName"
                required
                value={draft.fullName}
                onChange={(e) => set("fullName", e.target.value)}
              />
            </div>
            <div className="store-form-group store-form-group--phone-dial">
              <span id="addr-phone-label" className="store-form-label">
                Phone number
              </span>
              <div className="store-checkout-phone-row">
                <div className="store-checkout-dial-wrap" ref={dialWrapRef}>
                  <button
                    type="button"
                    className="store-checkout-dial-trigger"
                    aria-label="Country code"
                    aria-expanded={phoneDialOpen}
                    aria-haspopup="listbox"
                    aria-controls={dialListId}
                    onClick={() => setPhoneDialOpen((o) => !o)}
                  >
                    <ContactDialFlagImg
                      iso={dialMetaForCode(phoneDialCode).iso}
                      className="store-checkout-dial-trigger-flag"
                    />
                    <span className="store-checkout-dial-value">{phoneDialCode}</span>
                    <span className="store-checkout-dial-chevron" aria-hidden="true" />
                  </button>
                  {phoneDialOpen ? (
                    <ul
                      id={dialListId}
                      className="store-checkout-dial-list"
                      role="listbox"
                      aria-label="Country code"
                    >
                      {CONTACT_DIAL_OPTIONS.map(({ code, iso }) => (
                        <li key={code} role="none">
                          <button
                            type="button"
                            role="option"
                            className={
                              phoneDialCode === code
                                ? "store-checkout-dial-option is-active"
                                : "store-checkout-dial-option"
                            }
                            aria-selected={phoneDialCode === code}
                            onClick={() => {
                              setPhoneDialCode(code);
                              setPhoneDialOpen(false);
                            }}
                          >
                            <ContactDialFlagImg
                              iso={iso}
                              className="store-checkout-dial-option-flag"
                            />
                            <span className="store-checkout-dial-option-code">{code}</span>
                          </button>
                        </li>
                      ))}
                    </ul>
                  ) : null}
                </div>
                <input
                  id="addr-phone"
                  type="tel"
                  required
                  className="store-checkout-phone-national"
                  autoComplete="tel-national"
                  inputMode="numeric"
                  placeholder="5xx xxx xxx"
                  aria-labelledby="addr-phone-label"
                  value={draft.phone}
                  onChange={(e) =>
                    set("phone", e.target.value.replace(/[^\d\s]/g, ""))
                  }
                />
              </div>
            </div>
            <div className="store-form-group">
              <label htmlFor="addr-email" className="store-form-label">
                Email (optional)
              </label>
              <input
                id="addr-email"
                type="email"
                autoComplete="email"
                placeholder="For order updates"
                value={draft.email}
                onChange={(e) => set("email", e.target.value)}
              />
            </div>
            <div className="store-form-group">
              <label htmlFor="addr-city" className="store-form-label">
                City
              </label>
              <input
                id="addr-city"
                required
                value={draft.city}
                onChange={(e) => set("city", e.target.value)}
              />
            </div>
            <div className="store-form-group">
              <label htmlFor="addr-area" className="store-form-label">
                Area
              </label>
              <input
                id="addr-area"
                required
                value={draft.area}
                onChange={(e) => set("area", e.target.value)}
              />
            </div>
            <div className="store-form-group">
              <label htmlFor="addr-street" className="store-form-label">
                Street
              </label>
              <input
                id="addr-street"
                required
                value={draft.street}
                onChange={(e) => set("street", e.target.value)}
              />
            </div>
            <div className="store-form-group">
              <label htmlFor="addr-building" className="store-form-label">
                Building / apartment
              </label>
              <input
                id="addr-building"
                value={draft.building}
                onChange={(e) => set("building", e.target.value)}
              />
            </div>
            <div className="store-checkout-address-modal-map">
              <CheckoutMap
                latitude={draft.latitude}
                longitude={draft.longitude}
                onMapLocationUpdate={handleMapLocationUpdate}
                labels={{
                  title: t("store.checkout.mapTitle", {
                    defaultValue: "Delivery location on map",
                  }),
                  hint: t("store.checkout.mapHint", {
                    defaultValue:
                      "Search, use my location, or tap the map to save a pin with this address.",
                  }),
                  pinHint: guestMode
                    ? t("store.checkout.mapPinGuest", {
                        defaultValue:
                          "Pin is used for this order in this tab only and clears if you refresh the page.",
                      })
                    : t("store.checkout.mapPinSaved", {
                        defaultValue: "Pin is stored on this saved address.",
                      }),
                }}
              />
              {hasMapPin ? (
                <div className="store-checkout-map-actions store-checkout-address-modal-map-clear">
                  <button
                    type="button"
                    className="store-checkout-map-clear"
                    onClick={clearMapPin}
                  >
                    Clear map pin
                  </button>
                </div>
              ) : null}
            </div>
            <div className="store-form-group">
              <label htmlFor="addr-notes" className="store-form-label">
                Notes (optional)
              </label>
              <textarea
                id="addr-notes"
                rows={2}
                value={draft.notes}
                onChange={(e) => set("notes", e.target.value)}
              />
            </div>
            <p className="store-checkout-address-modal-hint">
              {guestMode
                ? t("store.checkout.guestMapHint", {
                    defaultValue:
                      "Map pin is optional. As a guest, nothing here is saved to your account—details stay in this page until you refresh or leave.",
                  })
                : t("store.checkout.savedMapHint", {
                    defaultValue:
                      "Map pin is optional; if you set one, it is used for this delivery address on orders.",
                  })}
            </p>
            <div className="store-checkout-address-modal-actions">
              <button
                type="button"
                className="store-checkout-address-modal-cancel"
                onClick={onClose}
                disabled={saving}
              >
                {t("store.checkout.addressModalCancel", { defaultValue: "Cancel" })}
              </button>
              <button
                type="submit"
                className="store-checkout-address-modal-save"
                disabled={saving}
              >
                {saving
                  ? guestMode
                    ? t("store.checkout.guestAddressApplying", {
                        defaultValue: "Applying…",
                      })
                    : t("store.checkout.addressSaving", { defaultValue: "Saving…" })
                  : guestMode
                    ? t("store.checkout.guestAddressUse", {
                        defaultValue: "Use this address",
                      })
                    : t("store.checkout.addressSave", { defaultValue: "Save address" })}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
